/**
 * usePortalSidebar Hook
 * Orchestrates sidebar data fetching via mediator and store updates.
 * Maps icon strings to Lucide components and handles navigation.
 */

import { logger, mediator } from '@/application/core';
import { GetSidebarMenuQuery, type SidebarMenuResponse } from '@/application/queries/sidebarMenu';
import { useTenant } from '@/components/common/UserContextProvider/UserContextProvider.store';
import { useIsMobile } from '@components/hooks/use-mobile';
import {
  navigatePortalInternal,
  useSetPortalLayoutSidebarOpen,
} from '../PortalLayout/PortalLayout.store';
import { mapMenuItemsWithIcons } from '@/utils/sidebar-icon-map';
import type { SidebarMenuItem } from '@/components/ui';
import { useCallback, useEffect, useMemo } from 'react';
import {
  usePortalSidebarCollapsedLogo,
  usePortalSidebarError,
  usePortalSidebarLoading,
  usePortalSidebarLogo,
  usePortalSidebarMenuItems,
  useSetPortalSidebarData,
  useSetPortalSidebarError,
  useSetPortalSidebarLoading,
} from './PortalSidebar.store';

/**
 * Hook return type
 */
interface UsePortalSidebarResult {
  mappedMenuItems: SidebarMenuItem[];
  logo: string;
  collapsedLogo: string;
  loading: boolean;
  error: Error | null;
  activePath: string;
  handleMenuItemClick: (item: SidebarMenuItem) => void;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch sidebar menu data and provide mapped items for rendering
 */
export function usePortalSidebar(): UsePortalSidebarResult {
  const menuItems = usePortalSidebarMenuItems();
  const sidebarLogo = usePortalSidebarLogo();
  const sidebarCollapsedLogo = usePortalSidebarCollapsedLogo();
  const tenant = useTenant();
  const loading = usePortalSidebarLoading();
  const error = usePortalSidebarError();
  const setData = useSetPortalSidebarData();
  const setLoading = useSetPortalSidebarLoading();
  const setError = useSetPortalSidebarError();
  const isMobile = useIsMobile();
  const setSidebarOpen = useSetPortalLayoutSidebarOpen();

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const result = await mediator.send<SidebarMenuResponse>(new GetSidebarMenuQuery());
      setData(result);
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Failed to fetch sidebar menu');
      setError(fetchError);
      logger.error('usePortalSidebar', 'Error:', err);
    }
  }, [setData, setLoading, setError]);

  useEffect(() => {
    // Only fetch if we don't have data yet (skip if already loading OR already have data)
    if (menuItems.length === 0) {
      fetchData();
    }
  }, [menuItems.length, fetchData]);

  // Prioritize tenant headerLogo over sidebar API logo; collapsed always uses default icon
  const logo = tenant?.headerLogo || sidebarLogo;
  const collapsedLogo = tenant?.headerLogo ? '/img/ecgridlogo_48x48.png' : sidebarCollapsedLogo;

  // Map icon strings to Lucide components
  const mappedMenuItems = useMemo(() => {
    return menuItems.length > 0 ? mapMenuItemsWithIcons(menuItems) : [];
  }, [menuItems]);

  // Determine active path from current URL
  const activePath = window.location.pathname;

  // Navigate on menu item click (server-side routing, no React Router)
  // On mobile, close sidebar drawer before navigating
  const handleMenuItemClick = useCallback(
    (item: SidebarMenuItem) => {
      if (item.path) {
        // Close sidebar on mobile before navigating
        if (isMobile) {
          setSidebarOpen(false);
        }

        // External links (full URLs) open in new tab
        if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
          window.open(item.path, '_blank', 'noopener,noreferrer');
          return;
        }

        navigatePortalInternal(item.path);
      }
    },
    [isMobile, setSidebarOpen],
  );

  return {
    mappedMenuItems,
    logo,
    collapsedLogo,
    loading,
    error,
    activePath,
    handleMenuItemClick,
    refetch: fetchData,
  };
}
